/**
 * Open a rodeo clip in CapCut on the user's device.
 *
 * There is no official CapCut REST API (as of 2026) — the closest thing is
 * the `capcut://` URL scheme which launches the mobile / desktop CapCut app.
 * It cannot pre-load a specific video, but we help the user out with 3 steps:
 *
 *   1. Fetch a 7-day presigned S3 URL for the clip (backend endpoint already
 *      exists — reuses `/api/clips/{id}/presigned-url`).
 *   2. Kick off a browser download of the .mp4 (Content-Disposition on the
 *      presigned URL forces "save" instead of "play in tab" where possible).
 *   3. Attempt to launch CapCut. If the app is installed the OS will hand
 *      control to it; if not, the user stays on the page and the download
 *      still completes.
 *
 * The user then opens CapCut → New Project → picks the freshly-saved file
 * from Photos / Recents / Downloads. Total time: ~10 seconds.
 */

import { api } from "@/lib/api";
import { toast } from "sonner";

/** True on small viewports OR touch-first devices (phones, tablets). */
export function isMobileDevice() {
  if (typeof window === "undefined") return false;
  const smallScreen = window.matchMedia("(max-width: 900px)").matches;
  const touch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return smallScreen || touch;
}

/** Trigger a browser file download (mobile Safari will still preview, but
 *  the "Save to Files/Photos" option appears). */
function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Try to open CapCut. Works if the app is installed on the device. */
function launchCapCut() {
  // capcut:// is CapCut's official iOS/Android/desktop scheme
  window.location.href = "capcut://";
}

/**
 * Full flow: presigned URL → download → open CapCut.
 * `kind` is "clip" | "reel" — chooses which backend endpoint to call.
 */
export async function openInCapCut(id, kind = "clip") {
  try {
    const endpoint = kind === "reel"
      ? `/reels/${id}/presigned-url`
      : `/clips/${id}/presigned-url`;
    const r = await api.get(endpoint);
    const { url, filename } = r.data;

    // 1. Copy URL to clipboard as a fallback (user can paste into
    //    "Documents by Readdle", Files.app, or any download manager)
    try {
      await navigator.clipboard.writeText(url);
    } catch { /* clipboard may be blocked on some mobile browsers — non-fatal */ }

    // 2. Kick off the browser download
    triggerDownload(url, filename);

    // 3. Show a short instruction toast so the user knows what to do next
    toast.success("Saving video · opening CapCut", {
      description: "In CapCut → tap New Project → pick this video from your Photos or Files",
    });

    // 4. Attempt to open CapCut after the download starts. Delay lets the
    //    browser initiate the download before the OS switches apps.
    setTimeout(launchCapCut, 1200);
  } catch (err) {
    toast.error(
      "Couldn't open in CapCut: " + (err?.response?.data?.detail || err.message),
    );
  }
}
